import { Token } from 'config/types'
import Column from 'layout/Components/Column'
import { Trans, useTranslation } from 'react-i18next'

import BigNumber from 'bignumber.js'
import { WithdrawFiatPaymentMethod } from 'config/types/payment'
import { ValidationError } from 'config/types/validator'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { RowBetween, RowCenter } from 'layout/Components/Row'
import React from 'react'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import Text from 'UIKit/Text'
import DepositRolloverOverview from 'views/Payment/components/DepositRollover/DepositRolloverOverview'
import ExtraInfo from 'views/Payment/components/ExtraInfo'
import TraditionalWithdrawButton from 'views/Payment/components/Withdraw/WithdrawByFiat/TraditionalWithdrawButton'
import WalletWithdrawButton from 'views/Payment/components/Withdraw/WithdrawByFiat/WalletWithdrawButton'
import BankSelectDropdown, { BankFormSelect } from 'views/Payment/DesktopPayment/components/BankSelectDropdown'
import {
  AmountDisplay,
  LockBonusAmountWithdrawOverview,
  PaymentFormControl,
  PaymentFormInput,
  PaymentFormInputAmount,
  PaymentLabel,
  PaymentStroke,
  StickyButtonWrapper,
} from '../../components/styles'
import FiatPaymentMethodModal, { FiatPaymentMethodModalProps } from '../../Deposit/DepositFiat/FiatPaymentMethodModal'
import { useWithdrawControls, WithdrawFiatControls, WithdrawModeEnums } from '../../hooks/useWithdraw'

interface WithdrawFiatProps {
  onBack: () => void
  onClose: () => void
  selectedToken: Token
}

const WithdrawFiat: React.FC<WithdrawFiatProps> = ({ selectedToken, onClose }) => {
  const { t } = useTranslation()
  const [onPresentPaymentMethods] =
    useModal<FiatPaymentMethodModalProps<WithdrawFiatPaymentMethod>>(FiatPaymentMethodModal)

  const {
    controls: {
      data: {
        returnedForm,
        selectedPaymentMethod,
        withdrawFee,
        totalDepositRollover,
        tokenBalanceAmount,
        lockAmount,
        totalWithdrawAmount,
        buttonPayload,
        methods: paymentMethods,
      },
      handler: {
        onSelectMethod,
        formatErrors,
        onValueChanged,
        onAmountChange,
        onConfirmWithraw,
        openDepositRolloverModal,
      },
      status: {
        isNotAbleToExecuteWithdraw,
        isWalletWithraw,
        isTraditionalWithdraw,
        isShowMobileNumberField,
        isShowLockAmount,
        isShowDepositRollover,
        isFetching,
      },
    },
  } = useWithdrawControls<WithdrawFiatControls>({
    mode: WithdrawModeEnums.FIAT,
    selectedToken,
  })

  const { isMobile } = useMatchBreakpoints()

  return (
    <Column gap="var(--pm-space-component, 20px)" height="100%">
      {isMobile ? (
        <BankFormSelect
          value={selectedPaymentMethod}
          onPresentMethodSelect={() => {
            onPresentPaymentMethods({
              methods: paymentMethods,
              onSelectMethod,
              selectedMethod: selectedPaymentMethod,
            })
          }}
        />
      ) : (
        <BankSelectDropdown<WithdrawFiatPaymentMethod>
          onSelect={onSelectMethod}
          options={paymentMethods}
          value={selectedPaymentMethod}
        />
      )}

      <PaymentFormControl
        disabled={!selectedPaymentMethod}
        state={returnedForm.states.bankAccountName}
        formatErrorMessage={(errors) =>
          formatErrors(errors, {
            [ValidationError.Required]: 'Bank account name is required',
          })
        }
        label={t('Bank Account Name')}
      >
        <PaymentFormInput
          tabIndex={1}
          control={{
            ...returnedForm.controls.bankAccountName,
            onValueChanged: (value, event) => onValueChanged('bankAccountName', value, event),
          }}
          placeholder={t('Enter your bank account name')}
          name="bankAccountName"
          maxLength={50}
          disabled={!selectedPaymentMethod}
        />
      </PaymentFormControl>

      <PaymentFormControl
        disabled={!selectedPaymentMethod}
        state={returnedForm.states.bankAccountNumber}
        formatErrorMessage={(errors) =>
          formatErrors(errors, {
            [ValidationError.Required]: 'Account number is required',
            [ValidationError.RequiredInteger]: 'Account number should be numeric',
          })
        }
        label={t('Bank Account Number')}
      >
        <PaymentFormInput
          type="number"
          tabIndex={0}
          placeholder={t('Enter your bank account number')}
          control={{
            ...returnedForm.controls.bankAccountNumber,
            onValueChanged: (value, event) => onValueChanged('bankAccountNumber', value, event),
          }}
          name="bankAccountNumber"
          maxLength={50}
          disabled={!selectedPaymentMethod}
        />
      </PaymentFormControl>

      {isShowMobileNumberField && (
        <PaymentFormControl
          state={returnedForm.states.holderMobileNumber}
          formatErrorMessage={(errors) =>
            formatErrors(errors, {
              [ValidationError.Required]: 'Mobile Number is required',
              [ValidationError.RequiredInteger]: 'Mobile Number should be numeric',
            })
          }
          label="Mobile Number"
          disabled={!selectedPaymentMethod}
        >
          <PaymentFormInput
            tabIndex={0}
            placeholder={t('Enter your mobile number')}
            control={{
              ...returnedForm.controls.holderMobileNumber,
              onValueChanged: (value, event) => onValueChanged('holderMobileNumber', value, event),
            }}
            name="holderMobileNumber"
            maxLength={30}
            disabled={!selectedPaymentMethod}
          />
        </PaymentFormControl>
      )}

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={returnedForm.states.amount}
          label={t('Amount')}
          formatErrorMessage={(errors) =>
            formatErrors(errors, { [ValidationError.Required]: 'Withdraw amount is required' })
          }
          disabled={!selectedPaymentMethod}
        >
          <PaymentFormInputAmount
            autoFocus={false}
            tabIndex={0}
            token={selectedToken}
            control={returnedForm.controls.amount}
            name="amount"
            disabled={!selectedPaymentMethod}
            value={returnedForm.states.amount.value}
            errors={!isFetching && returnedForm.states.amount.errors}
            onErrorChanged={returnedForm.controls.amount.onErrorChanged}
            onValueChanged={onAmountChange}
            max={
              selectedPaymentMethod?.maxAmount && tokenBalanceAmount.amount.gt(selectedPaymentMethod?.maxAmount)
                ? new BigNumber(selectedPaymentMethod?.maxAmount)
                : new BigNumber(tokenBalanceAmount.amount)
            }
            validateOnchange
            placeholder={returnedForm.states.amount.value || '0'}
            maxLength={8}
            isInteger
          />
        </PaymentFormControl>

        <AmountDisplay title="Available" amount={tokenBalanceAmount.amount} currency={selectedToken} />
      </Column>

      {selectedPaymentMethod && (
        <Column gap="var(--pm-space-label, 8px)">
          <PaymentLabel>
            <Trans>Withdraw Detail</Trans>
          </PaymentLabel>
          <Column gap="var(--pm-space-label, 8px)">
            <WithdrawInfoItem title="Withdraw amount">
              {Number(totalWithdrawAmount) > 0 &&
              tokenBalanceAmount.amount.gte(totalWithdrawAmount) &&
              tokenBalanceAmount.amount.gte(returnedForm.states.amount.value) ? (
                <>
                  {totalWithdrawAmount} {selectedToken.name}
                </>
              ) : (
                '-'
              )}
            </WithdrawInfoItem>

            <WithdrawInfoItem
              title={
                <>
                  {Number(selectedPaymentMethod?.feeAmount) > 0 &&
                    Number(selectedPaymentMethod?.feePercent) > 0 &&
                    t('Merchant fee ({{ percentFee }}% {{ amount }} {{ currency }})', {
                      percentFee: selectedPaymentMethod?.feePercent,
                      amount: selectedPaymentMethod?.feeAmount || '0',
                      currency: selectedPaymentMethod?.currency,
                    })}
                  {Number(selectedPaymentMethod?.feeAmount) === 0 &&
                    t('Merchant fee ({{ percentFee }}%)', {
                      percentFee: selectedPaymentMethod?.feePercent,
                    })}
                  {Number(selectedPaymentMethod?.feePercent) === 0 &&
                    t('Merchant fee ({{amount}} {{ currency }})', {
                      currency: selectedPaymentMethod?.currency,
                      amount: selectedPaymentMethod?.feeAmount || '0',
                    })}
                </>
              }
            >
              <RowCenter gap="6px">
                <Text
                  style={{
                    wordBreak: 'break-all',
                    textAlign: 'right',
                  }}
                  fontSize="1em"
                  color="currentColor"
                  bold
                >
                  {withdrawFee || '0'} {selectedToken.name}
                </Text>
                <Icons.HelpOutlineIcon
                  width="16px"
                  height="16px"
                  fill={colors.textSubtle}
                  data-tooltip-id={'common-tip-left'}
                  data-tooltip-content={t(
                    'If the calculated fee contains decimals, the fee will be rounded up to integer',
                  )}
                />
              </RowCenter>
            </WithdrawInfoItem>

            <WithdrawInfoItem title="Total withdraw amount">
              <Text fontSize="1em" color="warning" bold>
                {Number(returnedForm.states.amount.value) > 0 &&
                tokenBalanceAmount.amount.gte(returnedForm.states.amount.value) ? (
                  <>
                    {returnedForm.states.amount.value} {selectedToken.name}
                  </>
                ) : (
                  '-'
                )}
              </Text>
            </WithdrawInfoItem>
          </Column>
        </Column>
      )}

      <ExtraInfo gap="20px" hide={!isShowLockAmount && !isShowDepositRollover}>
        <PaymentStroke />
        <Column gap="8px">
          {isShowLockAmount && (
            <LockBonusAmountWithdrawOverview lockAmount={lockAmount} onClose={onClose} selectedToken={selectedToken} />
          )}
          {isShowDepositRollover && (
            <DepositRolloverOverview
              openDepositRollovermodal={openDepositRolloverModal}
              selectedToken={selectedToken}
              totalDepositRollover={totalDepositRollover}
            />
          )}
        </Column>
      </ExtraInfo>

      <StickyButtonWrapper>
        {isTraditionalWithdraw && (
          <TraditionalWithdrawButton
            width="100%"
            payload={buttonPayload}
            validateAll={returnedForm.validateAll}
            onSuccess={onConfirmWithraw}
            disabled={isNotAbleToExecuteWithdraw}
          />
        )}
        {isWalletWithraw && (
          <WalletWithdrawButton
            width="100%"
            payload={buttonPayload}
            validateAll={returnedForm.validateAll}
            onSuccess={onConfirmWithraw}
            disabled={isNotAbleToExecuteWithdraw}
          />
        )}
      </StickyButtonWrapper>
    </Column>
  )
}

const WithdrawInfoItem: React.FC<
  React.PropsWithChildren<{
    title: string | React.ReactNode
  }>
> = ({ title, children }) => {
  return (
    <RowBetween>
      <Text fontSize="12px" color="textAlt4">
        <Trans>{title}</Trans>
      </Text>
      <Text fontSize="12px" bold>
        {children}
      </Text>
    </RowBetween>
  )
}

export default WithdrawFiat
